import { defineStore } from "pinia";
import { ref } from "vue";
import { apiClient } from "@/client";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";

export interface PoolItem {
	id: number;
	createdAt: string;
	areaSqm: number;
	condition: string;
	geometry: any;
	reviewed: boolean;
	reviewerId: number | null;
	reviewedAt: string | null;
	deletedAt: string | null;
	deletorId: number | null;
}

export const usePoolStore = defineStore("pool", () => {
	const pools = ref<PoolItem[]>([]);
	const loading = ref(false);
	const error = ref<string | null>(null);

	SSEManager.subscribe((msg: SSEMessageResource) => {
		if (msg.resource !== "sync:pool") return;
		const poolId = uriID(msg.uri);
		if (!poolId) return;
		switch (msg.type) {
			case "created":
				fetchPool(poolId).then((p) => {
					if (p) pools.value.unshift(p);
				});
				break;
			case "updated":
				fetchPool(poolId).then((p) => {
					if (!p) return;
					const idx = pools.value.findIndex((x) => x.id === poolId);
					if (idx >= 0) pools.value[idx] = p;
				});
				break;
			case "deleted":
				pools.value = pools.value.filter((p) => p.id !== poolId);
				break;
		}
	});

	SSEManager.subscribeReconnect(() => {
		loadPools();
	});

	async function loadPools() {
		loading.value = true;
		error.value = null;
		try {
			pools.value =
				(await apiClient.JSONGet<PoolItem[]>("/api/vision/train/pool")) ?? [];
		} catch (e: any) {
			error.value = e?.message ?? "Failed to load pools";
		} finally {
			loading.value = false;
		}
	}

	async function loadPoolsInTile(z: number, x: number, y: number) {
		const ps = await apiClient.JSONGet<PoolItem[]>("/api/vision/train/pool", {
			params: { tile_z: z, tile_x: x, tile_y: y },
		});
		if (ps) {
			for (const p of ps) {
				const existing = pools.value.find((e) => e.id === p.id);
				if (!existing) pools.value.push(p);
			}
		}
	}

	async function fetchPool(id: number): Promise<PoolItem | null> {
		try {
			return await apiClient.JSONGet<PoolItem>(
				`/api/vision/train/pool/${encodeURIComponent(id)}`,
			);
		} catch (e: any) {
			console.error("Failed to fetch pool:", id, e);
			return null;
		}
	}

	async function deletePool(poolId: number) {
		await apiClient.JSONDelete(
			`/api/vision/train/pool/${encodeURIComponent(poolId)}`,
		);
		pools.value = pools.value.filter((p) => p.id !== poolId);
	}

	async function markPoolReviewed(poolId: number) {
		await apiClient.JSONPut(
			`/api/vision/train/pool/${encodeURIComponent(poolId)}`,
			{ reviewedAt: new Date().toISOString() },
		);
		const pool = pools.value.find((p) => p.id === poolId);
		if (pool) {
			pool.reviewed = true;
		}
	}

	async function addPoolDrawn(
		geometry: any,
		condition: string,
		itemId?: string,
	) {
		const resp = await apiClient.JSONPost<{ id: number }>(
			"/api/vision/train/pool",
			{
				geometry: geometry,
				condition: condition,
				...(itemId ? { itemId } : {}),
			},
		);
		if (resp?.id) {
			const pool = await fetchPool(resp.id);
			if (pool) pools.value.unshift(pool);
		}
	}

	return {
		pools,
		loading,
		error,
		loadPools,
		loadPoolsInTile,
		deletePool,
		addPoolDrawn,
		markPoolReviewed,
	};
});

function uriID(uri: string): number | null {
	const parts = uri.split("/");
	const last = parts[parts.length - 1];
	const id = parseInt(last, 10);
	return isNaN(id) ? null : id;
}
